// widgets/LabAnalysisWidget.tsx
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FileText, Sparkles, TrendingUp, TrendingDown, Minus, AlertCircle, CheckCircle, Brain } from "lucide-react";

const LabAnalysisWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const labTab = tabs.find((tab) => tab.id === "labAnalysis");
    const widget = labTab?.widget || {};

    // Get status icon and color
    const getStatusDisplay = (status: string) => {
        const displays: Record<string, { icon: React.ReactNode; color: string; bg: string }> = {
            high: { icon: <TrendingUp className="w-4 h-4" />, color: "text-red-600", bg: "bg-red-50" },
            low: { icon: <TrendingDown className="w-4 h-4" />, color: "text-blue-600", bg: "bg-blue-50" },
            normal: { icon: <Minus className="w-4 h-4" />, color: "text-green-600", bg: "bg-green-50" },
        };
        return displays[status] || displays.normal;
    };

    return (
        <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            {/* Main widget */}
            <div className="bg-gradient-to-br from-white/90 via-indigo-50/30 to-purple-100/20 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-indigo-200/40 mb-4 overflow-hidden relative">
                {/* Background effects */}
                <div className={`absolute ${isRTL ? "left-10" : "right-10"} -top-10 w-40 h-40 bg-gradient-to-br from-indigo-200 to-purple-100 rounded-full opacity-20 blur-2xl`}></div>

                {/* Header */}
                <div className="flex justify-between items-center mb-6 relative z-10">
                    <div className="flex items-center">
                        <motion.div
                            className={`me-3 p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg`}
                            animate={{ rotate: [0, 360] }}
                            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        >
                            <Brain className="w-6 h-6 text-white" />
                        </motion.div>
                        <div>
                            <h3 className="text-xl font-bold text-indigo-900 mb-1">{widget.title}</h3>
                            <p className="text-sm text-indigo-600">{widget.subtitle}</p>
                        </div>
                    </div>
                    <motion.div
                        className="flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2 rounded-2xl shadow-lg"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <Sparkles className="w-4 h-4" />
                        <span className="text-sm font-semibold">{widget.aiPowered}</span>
                    </motion.div>
                </div>

                {/* Upload date & status */}
                <div className="mb-6 p-4 bg-white/60 backdrop-blur-md rounded-2xl border border-indigo-200/50 relative z-10">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-indigo-600" />
                            <div>
                                <p className="text-sm font-semibold text-gray-800">{widget.uploadInfo?.label}</p>
                                <p className="text-xs text-gray-600">{widget.uploadInfo?.date}</p>
                            </div>
                        </div>
                        <motion.div
                            className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.5, type: "spring" }}
                        >
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-xs font-semibold">{widget.uploadInfo?.status}</span>
                        </motion.div>
                    </div>
                </div>

                {/* Lab results */}
                <div className="space-y-3 mb-6 relative z-10">
                    <h4 className="text-sm font-semibold text-indigo-900 mb-4 flex items-center">
                        <FileText className={`w-4 h-4 me-2 text-indigo-600`} />
                        {widget.results?.title}
                    </h4>

                    {widget.results?.items?.map((result: any, index: number) => {
                        const statusDisplay = getStatusDisplay(result.status);

                        return (
                            <motion.div
                                key={index}
                                className="p-4 bg-white/50 backdrop-blur-sm rounded-xl border border-indigo-100/50"
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.6 + index * 0.1 }}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-lg ${statusDisplay.bg} flex items-center justify-center ${statusDisplay.color}`}>{statusDisplay.icon}</div>
                                        <div>
                                            <h5 className="text-sm font-semibold text-gray-800">{result.name}</h5>
                                            <p className="text-xs text-gray-500">{result.category}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-lg font-bold text-gray-800">{result.value}</div>
                                        <div className="text-xs text-gray-500">{result.unit}</div>
                                    </div>
                                </div>

                                {/* Range bar */}
                                <div className="relative mt-3">
                                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                        {/* Normal range indicator */}
                                        <div
                                            className="absolute h-full bg-green-200"
                                            style={{ left: `${result.normalRange?.start}%`, width: `${result.normalRange?.end - result.normalRange?.start}%` }}
                                        />

                                        {/* Current value indicator */}
                                        <motion.div
                                            className="absolute top-0 w-1 h-full bg-indigo-600"
                                            initial={{ left: "0%" }}
                                            animate={{ left: `${result.position}%` }}
                                            transition={{ duration: 1, delay: 0.8 + index * 0.1 }}
                                        />
                                    </div>
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                        <span>{result.range?.min}</span>
                                        <span className="text-green-600 font-medium">{widget.normalLabel}</span>
                                        <span>{result.range?.max}</span>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* AI Insights */}
            <motion.div
                className="bg-gradient-to-br from-indigo-50/60 to-purple-50/40 backdrop-blur-xl rounded-2xl p-4 border border-indigo-200/50 shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
            >
                <div className="flex items-center mb-3">
                    <motion.div
                        className={`w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center me-2 shadow-lg`}
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                    >
                        <Sparkles className="w-5 h-5 text-white" />
                    </motion.div>
                    <div>
                        <h4 className="font-bold text-indigo-900">{widget.insights?.title}</h4>
                        <p className="text-xs text-indigo-600">{widget.insights?.subtitle}</p>
                    </div>
                </div>

                {/* Overall assessment */}
                <div className="mb-4 p-3 bg-white/60 backdrop-blur-sm rounded-xl">
                    <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                            <h5 className="text-sm font-semibold text-gray-800 mb-1">{widget.insights?.assessment?.title}</h5>
                            <p className="text-sm text-gray-700">{widget.insights?.assessment?.text}</p>
                        </div>
                    </div>
                </div>

                {/* Recommendations */}
                <div className="space-y-2">
                    <h5 className="text-sm font-semibold text-indigo-900 mb-2">{widget.insights?.recommendations?.title}</h5>
                    {widget.insights?.recommendations?.items?.map((rec: any, index: number) => (
                        <motion.div
                            key={index}
                            className="flex items-start gap-2 p-3 bg-white/60 backdrop-blur-sm rounded-lg"
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.7 + index * 0.1 }}
                        >
                            <div className={`w-6 h-6 mt-0.5 rounded-full ${rec.priority === "high" ? "bg-red-100" : "bg-indigo-100"} flex items-center justify-center flex-shrink-0`}>
                                {rec.priority === "high" ? <AlertCircle className="w-3.5 h-3.5 text-red-600" /> : <CheckCircle className="w-3.5 h-3.5 text-indigo-600" />}
                            </div>
                            <p className="text-sm text-gray-700">{rec.text}</p>
                        </motion.div>
                    ))}
                </div>

                {/* Consult doctor notice */}
                {widget.consultNotice && (
                    <motion.div
                        className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.2 }}
                    >
                        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-amber-800">{widget.consultNotice}</p>
                    </motion.div>
                )}
            </motion.div>
        </motion.div>
    );
};

export default LabAnalysisWidget;
